import { CalendarTodayOutlined, WarningAmber, AccessTime } from '@mui/icons-material';
import { CardContainer, Tag } from 'finara-components';

import type { ScheduledOperation, CriticalAlert, CriticalEvent } from '@/types/analytics';

interface ScheduledOperationsCardProps {
  operations?: ScheduledOperation[];
}

interface CriticalAlertsCardProps {
  alerts?: CriticalAlert[];
}

interface CriticalEventsCardProps {
  events?: CriticalEvent[];
}

const formatAmount = (amount: number) => {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
  return `$${amount.toLocaleString()}`;
};

const getPriorityColor = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'critical':
      return 'bg-red-500';
    case 'warning':
      return 'bg-yellow-500';
    case 'info':
      return 'bg-green-500';
    default:
      return 'bg-black/20 dark:bg-white/40';
  }
};

export const ScheduledOperationsCard: React.FC<ScheduledOperationsCardProps> = ({
  operations = [],
}) => {
  return (
    <CardContainer
      title="Operaciones Programadas Hoy"
      icon={<CalendarTodayOutlined className="mr-2 text-brand-white" fontSize="small" />}
    >
      {operations.length === 0 ? (
        <div className="text-center py-8 text-muted text-sm">
          No hay operaciones programadas para hoy
        </div>
      ) : (
        <div className="space-y-2.5">
          {operations.slice(0, 3).map(operation => (
            <div key={operation.id} className="flex justify-between items-center">
              <div>
                <div className="text-[12.5px] font-medium text-brand-white">
                  {operation.organizationName}
                </div>
                <div className="flex gap-2 items-center">
                  <Tag variant="white">{operation.type}</Tag>
                  <span className="text-[10.25px] text-muted">
                    {formatAmount(operation.amount)}
                  </span>
                </div>
              </div>
              <div className="text-[12.25px] font-medium">{operation.scheduledTime}</div>
            </div>
          ))}
        </div>
      )}
    </CardContainer>
  );
};

export const CriticalAlertsCard: React.FC<CriticalAlertsCardProps> = ({ alerts = [] }) => {
  return (
    <CardContainer
      title="Alertas Críticas"
      icon={<WarningAmber className="mr-2 text-red-500" fontSize="small" />}
    >
      {alerts.length === 0 ? (
        <div className="text-center py-8 text-muted text-sm">
          No hay alertas críticas en este momento
        </div>
      ) : (
        <div className="space-y-2.5">
          {alerts.slice(0, 3).map(alert => (
            <div key={alert.id} className="flex items-start space-x-2">
              <div
                className={`w-2 h-2 ${getPriorityColor(alert.priority)} rounded-full mt-2 flex-shrink-0`}
              ></div>
              <div className="flex-1">
                <div className="flex gap-2 items-center mb-1 flex-wrap">
                  <Tag variant="white">{alert.category}</Tag>
                  <span className="text-[10.5px] text-muted">
                    {alert.minutesAgo} min{alert.minutesAgo !== 1 ? '' : ''} ago
                  </span>
                </div>
                <div className="text-[12.25px] text-brand-white">{alert.message}</div>
                {alert.organizationName && (
                  <div className="text-[10.5px] text-muted mt-1">{alert.organizationName}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContainer>
  );
};

export const CriticalEventsCard: React.FC<CriticalEventsCardProps> = ({ events = [] }) => {
  return (
    <CardContainer
      title="Últimos Eventos Críticos"
      icon={<AccessTime className="mr-2 text-brand-white" fontSize="small" />}
    >
      {events.length === 0 ? (
        <div className="text-center py-8 text-muted text-sm">
          No hay eventos críticos recientes
        </div>
      ) : (
        <div className="space-y-2.5">
          {events.slice(0, 3).map((event, index) => (
            <div
              key={event.id}
              className={`flex justify-between items-center py-[7px] ${
                index < events.length - 1 ? 'border-b border-b-border' : ''
              }`}
            >
              <div>
                <div className="font-medium text-brand-white text-[12.5px]">{event.title}</div>
                <div className="text-[12.25px] text-muted">{event.organizationName}</div>
              </div>
              <div className="text-[10.25px] text-muted">
                hace {event.hoursAgo} hora{event.hoursAgo !== 1 ? 's' : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContainer>
  );
};
